// @flow
import type { Errors } from 'types/errors';
import type { RequestStates } from 'types/redux';
import type { Passenger, Passengers } from 'types/entities';
import type { Booking, Place } from 'types/bookings';
import type { PassengerLibraryState } from 'reducers/library';

import _ from 'lodash';
import classnames from 'classnames';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { cohortPassengerAssociate } from 'actions/cohorts';
import {
  passengerLogin,
  passengerLogout,
  passengerDelete,
  passengerBookingGet,
  passengerBookingCancel,
  passengerBookingDelete,
  passengerChangeDestination,
} from 'actions/passengers';
import {
  showPassengersModal,
  showPassengerEditModal,
  setActiveBooking,
  unsetActiveBooking,
  forwardGeo,
} from 'actions/app';
import { resetErrorState } from 'actions/helpers';
import { PASSENGER_BEHAVIOURS_NAMES } from 'utils/library-utils';
import { entityURLForCohort } from 'utils/cohort-utils';
import {
  canBookingBeCancelledByPassenger,
  canDestinationBeChanged,
  bookingStateString,
} from 'utils/booking-utils';

import ErrorsNotification from 'components/others/ErrorNotification';
import PowerButton from 'components/buttons/PowerButton';
import PassengersModal from './PassengersModal';
import PassengerEditModal from './PassengerEditModal';
import CohortLocationModal from '../../../components/others/CohortLocationModal';

type Props = {
  activeCohortID: string,
  activeBookingID: string,
  activePassengerID: string,
  passengers: Passengers,
  libraryPassengers: PassengerLibraryState,

  cohortPassengerAssociate: Function,
  cohortPassengerAssociateRequest: RequestStates,
  cohortPassengerAssociateErrors: Errors,

  passengerLogin: Function,
  passengerLoginRequest: RequestStates,
  passengerLoginErrors: Errors,

  passengerLogout: Function,
  passengerLogoutRequest: RequestStates,
  passengerLogoutErrors: Errors,

  passengerDelete: Function,
  passengerDeleteRequest: RequestStates,
  passengerDeleteErrors: Errors,

  passengerBookingGet: Function,
  passengerBookingGetRequest: RequestStates,
  passengerBookingGetErrors: Errors,

  passengerBookingCancel: Function,
  passengerBookingCancelRequest: RequestStates,
  passengerBookingCancelErrors: Errors,

  passengerBookingDelete: Function,

  showPassengersModal: Function,
  isPassengersModalActive: boolean,

  showPassengerEditModal: Function,
  passengerBeingEdited: string,

  setActiveBooking: Function,
  unsetActiveBooking: Function,

  resetErrorState: Function,

  onPassengerBookingClick: Function,
  bookingInProgress: boolean,

  forwardGeo: Function,
  passengerChangeDestination: Function,
  locations: Array<any>,

  passengerChangeDestinationRequest: RequestStates,
  passengerChangeDestinationErrors: Errors,
};

type State = {
  isCollapsed: boolean,
  changeDestination: boolean,
  pickupPoint: ?Place,
};

const requestErrors = [
  'cohortPassengerAssociate',
  'passengerLogin',
  'passengerLogout',
  'passengerDelete',
];

export class PassengersPanel extends Component {
  constructor(props: Props) {
    super(props);
    this.state = {
      isCollapsed: false,
      changeDestination: false,
      pickupPoint: null,
    };
  }

  state: State;

  componentDidMount() {
    this.props.resetErrorState(requestErrors);
  }

  props: Props;

  toggleLocationSelection(point: ?Place) {
    this.setState({
      changeDestination: !this.state.changeDestination,
      pickupPoint: point,
    });
  }

  render() {
    const numPassengers: number = Object.keys(this.props.passengers).length;
    return (
      <div className="entity-panel">
        <button
          className="button is-primary is-small is-pulled-right entity-add-button"
          title="Associate passenger to cohort"
          aria-label="Associate passenger to cohort"
          onClick={(event) => {
            event.stopPropagation();
            this.props.showPassengersModal(true);
          }}
        >
          <span className="icon is-small" aria-hidden="true">
            <i className="fa fa-plus" />
          </span>
        </button>
        <h3 className="title section-title is-6 entity-panel-title">
          <button
            aria-label="Toggle panel"
            onClick={() => {
              this.setState({
                isCollapsed: !this.state.isCollapsed,
              });
            }}
          >
            <span className="icon is-small">
              <i
                className={classnames('fa fa-fw', {
                  'fa-chevron-down': !this.state.isCollapsed,
                  'fa-chevron-right': this.state.isCollapsed,
                })}
              />
            </span>
            {' '}
            Passengers {!!numPassengers && `(${numPassengers})`}
          </button>
        </h3>
        {this.props.isPassengersModalActive &&
          <PassengersModal
            {...this.props}
            handleClose={() => { this.props.showPassengersModal(false); }}
            passengers={this.props.libraryPassengers}
          />
        }
        {!!this.props.passengerBeingEdited &&
          <PassengerEditModal
            cohortID={this.props.activeCohortID}
            passengerID={this.props.passengerBeingEdited}
            handleClose={() => { this.props.showPassengerEditModal(null); }}
            showModal={!!this.props.passengerBeingEdited}
          />
        }
        {this.state.changeDestination && <CohortLocationModal
          showModal="true"
          cohortSetLocation={(latlng) => {
            const {
              activeCohortID,
              activePassengerID,
              activeBookingID,
              passengerChangeDestination: changeDestination,
            } = this.props;
            changeDestination(activeCohortID, activePassengerID, activeBookingID, _.map(latlng.split(','), Number));
          }}
          onAutoCompleteLocation={(locationQuery: String) => {
            if (!this.state.pickupPoint) {
              this.props.forwardGeo(locationQuery, '');
              return;
            }

            const {
              longitude: lng,
              latitude: lat,
            } = this.state.pickupPoint;
            this.props.forwardGeo(locationQuery, '', [lng, lat]);
          }}
          handleClose={() => {
            this.toggleLocationSelection(null);
          }}
          autoCompleteLocations={this.props.locations}
        />}
        {!this.state.isCollapsed &&
          <div>
            {!_.isEmpty(this.props.passengerLoginErrors) &&
              <ErrorsNotification
                errors={this.props.passengerLoginErrors}
                dismissible
                onClose={() => {
                  this.props.resetErrorState('passengerLogin');
                }}
              />
            }
            {!_.isEmpty(this.props.passengerLogoutErrors) &&
              <ErrorsNotification
                errors={this.props.passengerLogoutErrors}
                dismissible
                onClose={() => {
                  this.props.resetErrorState('passengerLogout');
                }}
              />
            }
            {!_.isEmpty(this.props.passengerDeleteErrors) &&
              <ErrorsNotification
                errors={this.props.passengerDeleteErrors}
                dismissible
                onClose={() => {
                  this.props.resetErrorState('passengerDelete');
                }}
              />
            }
            {!_.isEmpty(this.props.passengerBookingCancelErrors) &&
              <ErrorsNotification
                errors={this.props.passengerBookingCancelErrors}
                dismissible
                onClose={() => {
                  this.props.resetErrorState('passengerBookingCancel');
                }}
              />
            }
            {!_.isEmpty(this.props.passengerChangeDestinationErrors) &&
              <ErrorsNotification
                errors={this.props.passengerChangeDestinationErrors}
                dismissible
                onClose={() => {
                  this.props.resetErrorState('passengerChangeDestination');
                }}
              />
            }
            <ul>
              {numPassengers ?
                Object.keys(this.props.passengers).map((passengerID: string) => {
                  const passenger: Passenger = this.props.passengers[passengerID];
                  const disableButtons: boolean = this.props.passengerLoginRequest.isPending ||
                    this.props.passengerLogoutRequest.isPending ||
                    this.props.passengerDeleteRequest.isPending;
                  const canMakeBooking: boolean = passenger.loggedIn &&
                    !_.get(passenger.behaviour, 'type');
                  return (
                    <li key={passengerID} className="entity-row">
                      <div className="field is-grouped is-pulled-right">
                        <div className="control">
                          <span title="Passenger is logged in">
                            <PowerButton
                              disabled={disableButtons}
                              active={passenger.loggedIn}
                              onClick={() => {
                                if (passenger.loggedIn) {
                                  this.props.passengerLogout(this.props.activeCohortID,
                                    passengerID);
                                } else {
                                  this.props.passengerLogin(this.props.activeCohortID,
                                    passengerID, passenger);
                                }
                                this.props.resetErrorState(requestErrors);
                              }}
                              label={passenger.loggedIn ? 'Logout passenger' : 'Login passenger'}
                            />
                          </span>
                        </div>
                        {!passenger.loggedIn &&
                          <p className="control">
                            <button
                              className="button is-small is-outlined is-info"
                              title="Edit passenger"
                              aria-label="Edit passenger"
                              onClick={() => {
                                this.props.showPassengerEditModal(passengerID);
                              }}
                            >
                              <span className="icon is-small" aria-hidden="true">
                                <i className="fa fa-pencil" />
                              </span>
                            </button>
                          </p>
                        }
                        {canMakeBooking &&
                          <p className="control">
                            <button
                              className={classnames('button is-small is-outlined is-info', {
                                'is-disabled': this.props.bookingInProgress,
                              })}
                              title="Create booking"
                              aria-label="Create booking"
                              onClick={() => {
                                this.props.onPassengerBookingClick(passengerID);
                              }}
                            >
                              <span className="icon is-small" aria-hidden="true">
                                <i className="fa fa-book" />
                              </span>
                            </button>
                          </p>
                        }
                        <p className="control">
                          <button
                            className={classnames('button is-small is-danger is-outlined', {
                              'is-disabled': disableButtons,
                              'is-loading': this.props.passengerDeleteRequest.isPending,
                            })}
                            title="Unassociate passenger"
                            aria-label="Unassociate passenger"
                            onClick={() => {
                              /* eslint-disable no-alert */
                              if (confirm('Are you sure you want to remove Passenger ' +
                                `${passengerID} from the cohort?`)) {
                                this.props.passengerDelete(this.props.activeCohortID,
                                  passengerID);
                                this.props.resetErrorState(requestErrors);
                              }
                            }}
                          >
                            <span className="icon is-small" aria-hidden="true">
                              <i className="fa fa-trash" />
                            </span>
                          </button>
                        </p>
                      </div>
                      <p className="entity-title">
                        Passenger ID: {passengerID}
                      </p>
                      <div className="entity-details-section">
                        <div>
                          Gamma ID: {passenger.cohortPassengerID}{' '}
                          <a
                            href={`${entityURLForCohort()}/passengers/${passenger.cohortPassengerID}`}
                            target="_blank"
                            title="View passenger in Gamma"
                            aria-label="View passenger in Gamma"
                            rel="noopener noreferrer"
                          >
                            <span className="icon is-small" aria-hidden="true">
                              <i className="fa fa-external-link" />
                            </span>
                          </a>
                          {' '}&middot;{' '}
                          {PASSENGER_BEHAVIOURS_NAMES[_.get(passenger, 'behaviour.type')]}
                        </div>
                        {!!passenger.bookings && !!passenger.bookings.length &&
                          <div>
                            <ul className="entity-bookings">
                              {passenger.bookings.map((booking: Booking) => {
                                const bookingID: string = booking.bookingID;
                                return (
                                  <li
                                    key={bookingID}
                                    className={classnames('entity-booking-row', {
                                      active: this.props.activeBookingID === bookingID,
                                    })}
                                    onClick={() => {
                                      if (this.props.activeBookingID === bookingID) {
                                        this.props.unsetActiveBooking();
                                      } else {
                                        this.props.passengerBookingGet(this.props.activeCohortID,
                                          passengerID, bookingID);
                                        this.props.setActiveBooking({
                                          passengerID,
                                          bookingID,
                                        });
                                      }
                                    }}
                                  >
                                    <div
                                      className="field is-grouped is-pulled-right"
                                      onClick={(event) => {
                                        event.stopPropagation();
                                      }}
                                    >
                                      <p className="control">
                                        {canBookingBeCancelledByPassenger(booking.state) &&
                                          <button
                                            className={classnames('button is-small is-outlined is-warning', {
                                              'is-loading': this.props.passengerBookingCancelRequest.isPending,
                                              'is-disabled': this.props.passengerBookingCancelRequest.isPending,
                                            })}
                                            title="Cancel booking"
                                            onClick={() => {
                                              this.props.passengerBookingCancel(
                                                this.props.activeCohortID, passengerID, bookingID);
                                            }}
                                          >
                                            CANCEL
                                          </button>
                                        }
                                      </p>
                                      <p className="control">
                                        {canDestinationBeChanged(booking.state) &&
                                          <button
                                            className={classnames('button is-small is-outlined is-warning', {
                                              'is-loading': this.props.passengerChangeDestinationRequest.isPending,
                                              'is-disabled': this.props.passengerChangeDestinationRequest.isPending,
                                            })}
                                            title="Change Destination"
                                            onClick={() => {
                                              this.toggleLocationSelection(booking.itinerary[0]);
                                              this.props.setActiveBooking({ bookingID, passengerID });
                                            }}
                                          >
                                            CHANGE DESTINATION
                                          </button>
                                        }
                                      </p>
                                      <p className="control">
                                        <button
                                          className="button is-small is-outlined is-danger"
                                          title="Remove booking"
                                          aria-label="Remove booking"
                                          onClick={() => {
                                            this.props.passengerBookingDelete(
                                              this.props.activeCohortID, passengerID, bookingID);
                                          }}
                                        >
                                          <span className="icon is-small" aria-hidden="true">
                                            <i className="fa fa-trash" />
                                          </span>
                                        </button>
                                      </p>
                                    </div>
                                    <p className="entity-title">{bookingID}</p>
                                    <p>
                                      <span className="tag is-small">
                                        {bookingStateString(booking.state)}
                                      </span>
                                      {booking.driverID &&
                                        <span>
                                          {' '}&middot;{' '}
                                          Driver {booking.driverID}
                                        </span>
                                      }
                                    </p>
                                  </li>
                                );
                              })}
                            </ul>
                          </div>
                        }
                      </div>
                    </li>
                  );
                })
                :
                <li className="entity-row">No passengers associated to this cohort.</li>
              }
            </ul>
          </div>
        }
      </div>
    );
  }
}

function mapStateToProps(state) {
  const activeCohortID: string = state.app.activeCohortID;
  const activeBookingID: string = _.get(state.app, 'activeBooking.bookingID');
  const activePassengerID: string = _.get(state.app, 'activeBooking.passengerID');
  const libraryPassengers: PassengerLibraryState = state.library.passengers || {};
  const passengers: Passengers = _.get(state.cohorts[activeCohortID], 'passengers', {});
  const cohortPassengerIDs: Array<string> = _.map(passengers, (pax: Passenger) => pax.cohortPassengerID);
  // Remove the already associated passengers.
  const filteredLibraryPassengers: Passengers = _.omit(libraryPassengers, cohortPassengerIDs);

  return {
    activeCohortID,
    activeBookingID,
    activePassengerID,
    isPassengersModalActive: state.app.showPassengersModal,
    passengerBeingEdited: state.app.passengerBeingEdited,

    libraryPassengers: filteredLibraryPassengers,
    passengers,

    cohortPassengerAssociateRequest: state.requests.cohortPassengerAssociateRequest || {},
    cohortPassengerAssociateErrors: _.get(state.errors, 'cohortPassengerAssociate.errors', []),

    passengerLoginRequest: state.requests.passengerLoginRequest || {},
    passengerLoginErrors: _.get(state.errors, 'passengerLogin.errors', []),

    passengerLogoutRequest: state.requests.passengerLogoutRequest || {},
    passengerLogoutErrors: _.get(state.errors, 'passengerLogout.errors', []),

    passengerDeleteRequest: state.requests.passengerDeleteRequest || {},
    passengerDeleteErrors: _.get(state.errors, 'passengerDelete.errors', []),

    passengerBookingGetRequest: state.requests.passengerBookingGetRequest || {},
    passengerBookingGetErrors: _.get(state.errors, 'passengerBookingGet.errors', []),

    passengerBookingCancelRequest: state.requests.passengerBookingCancelRequest || {},
    passengerBookingCancelErrors: _.get(state.errors, 'passengerBookingCancel.errors', []),

    locations: state.app.autoComplete,

    passengerChangeDestinationRequest: state.requests.passengerChangeDestinationRequest || {},
    passengerChangeDestinationErrors: _.get(state.errors, 'passengerChangeDestination.errors', []),
  };
}

export default connect(
  mapStateToProps,
  {
    cohortPassengerAssociate,
    passengerLogin,
    passengerLogout,
    passengerDelete,

    passengerBookingGet,
    passengerBookingCancel,
    passengerBookingDelete,
    passengerChangeDestination,

    showPassengersModal,
    showPassengerEditModal,

    setActiveBooking,
    unsetActiveBooking,

    resetErrorState,
    forwardGeo,
  }
)(PassengersPanel);



/** WEBPACK FOOTER **
 ** ./src/js/views/dashboard/passengers/PassengersPanel.jsx
 **/